/**
 * Membership role for a user on a team (`users.user_team`).
 * Stored as PostgreSQL enum `users.user_team_membership_role`.
 */
export enum TeamRole {
  VIEWER = 'VIEWER',
  MEMBER = 'MEMBER',
  TEAM_SUPERVISOR = 'TEAM_SUPERVISOR',
  MANAGER = 'MANAGER',
}

/** Roles that participate as a full team guide (assigned-guide lane, phone routing, cross-guide access). */
export const TEAM_GUIDE_PARTICIPANT_ROLES: readonly TeamRole[] = [
  TeamRole.MEMBER,
  TeamRole.TEAM_SUPERVISOR,
  TeamRole.MANAGER,
];

/**
 * When a guide has {@link TEAM_GUIDE_PARTICIPANT_ROLES} on more than one team, pick the team using this
 * role priority first, then lowest `team.id` within the same role. MANAGER wins first so the
 * responsible lead team stays ahead of member or advisory TEAM_SUPERVISOR seats during reassignment
 * (e.g. deleted guide).
 */
export const TEAM_GUIDE_PRIMARY_TEAM_ROLE_ORDER: readonly TeamRole[] = [
  TeamRole.MANAGER,
  TeamRole.MEMBER,
  TeamRole.TEAM_SUPERVISOR,
];

/** Roles with manager-equivalent permissions (legacy `isManager` behavior). */
export const TEAM_ELEVATED_LEAD_ROLES: readonly TeamRole[] = [
  TeamRole.TEAM_SUPERVISOR,
  TeamRole.MANAGER,
];

/**
 * Builds a PostgreSQL `IN (...)` label list for `users.user_team.role::text`.
 * Only for fixed {@link TeamRole} sets — never pass request or user input.
 *
 * @param roles - Enum labels to include.
 * @returns SQL fragment, e.g. `('MEMBER', 'MANAGER')`.
 */
function teamRoleLabelsSqlIn(roles: readonly TeamRole[]): string {
  return `(${roles.map(role => `'${role}'`).join(', ')})`;
}

/**
 * Whether this team role counts as a “full” guide participant (not a viewer-only seat).
 * @param role - `TeamRole` from `user_team`.
 * @returns True for MEMBER, TEAM_SUPERVISOR, and MANAGER.
 */
export function isTeamGuideParticipantRole(role: TeamRole): boolean {
  return TEAM_GUIDE_PARTICIPANT_ROLES.includes(role);
}

/**
 * Whether this team role carries elevated lead permissions (legacy `isManager`).
 * @param role - `TeamRole` from `user_team`.
 * @returns True for TEAM_SUPERVISOR and MANAGER.
 */
export function isTeamElevatedLeadRole(role: TeamRole): boolean {
  return TEAM_ELEVATED_LEAD_ROLES.includes(role);
}

/**
 * SQL fragment for `IN (...)` against `users.user_team.role` (PostgreSQL enum labels).
 * Use inside raw SQL strings only.
 */
export const USER_TEAM_GUIDE_PARTICIPANT_ROLE_SQL_IN = teamRoleLabelsSqlIn(
  TEAM_GUIDE_PARTICIPANT_ROLES
);

/**
 * SQL fragment for elevated lead roles (legacy `isManager`).
 */
export const USER_TEAM_ELEVATED_LEAD_ROLE_SQL_IN = teamRoleLabelsSqlIn(
  TEAM_ELEVATED_LEAD_ROLES
);

/** Quoted label for `role::text = …` (operational {@link TeamRole.MEMBER} team). */
export const USER_TEAM_MEMBER_ROLE_SQL = teamRoleLabelsSqlIn([TeamRole.MEMBER]);

/** Quoted label for `role::text IN (...)` (operational {@link TeamRole.MANAGER} team). */
export const USER_TEAM_MANAGER_ROLE_SQL_IN = teamRoleLabelsSqlIn([
  TeamRole.MANAGER,
]);
/** Every label on `users.user_team_membership_role` (replaces legacy `ut.role IN (1, 2)` plus elevated roles). */
export const USER_TEAM_ALL_MEMBERSHIP_ROLE_SQL_IN = teamRoleLabelsSqlIn(
  Object.values(TeamRole)
);

/**
 * Human-readable label for admin UI and tables.
 * @param role - `TeamRole` value.
 * @returns Short display string.
 */
export function formatTeamMembershipRoleLabel(role: TeamRole): string {
  const labels: Record<TeamRole, string> = {
    [TeamRole.VIEWER]: 'Viewer',
    [TeamRole.MEMBER]: 'Member',
    [TeamRole.TEAM_SUPERVISOR]: 'Team supervisor',
    [TeamRole.MANAGER]: 'Manager',
  };
  return labels[role] ?? String(role);
}
